import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import { dataSearchState } from "../state";
import {
  DEPARTMENT_FUNCTION_OPTIONS,
  DEPARTMENT_ID_TO_LABEL
} from "./employeesByDepartmentData";

/**
 * Single-select department dropdown. value = selected id or null. excludedIds = ids selected in other cards (shown greyed out, not selectable).
 * Trigger shows selected label when set. Chip below shows "Department: Min-Max" with remove.
 */
const DepartmentSelect = ({
  value = null,
  onChange,
  excludedIds = [],
  start = null,
  end = null,
  onRemoveChip,
  placeholder = "Department",
  searchPlaceholder = "Search",
  defaultOpen = false,
  showChip = true
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const excludedSet = new Set(excludedIds);
  const baseOptions = DEPARTMENT_FUNCTION_OPTIONS;
  const filtered = search.trim()
    ? baseOptions.filter(
        o =>
          (o.label || "").toLowerCase().includes(search.toLowerCase()) ||
          (o.id || "").toLowerCase().includes(search.toLowerCase())
      )
    : baseOptions;

  const selectOne = id => {
    if (excludedSet.has(id) && id !== value) return;
    onChange(id);
    setOpen(false);
  };

  const label = value ? (DEPARTMENT_ID_TO_LABEL[value] || value) : null;
  const rangeStr = [start != null ? start : "", end != null ? end : ""].filter(s => s !== "").join("-");
  const chipText = rangeStr ? `${label}: ${rangeStr}` : label;

  return (
    <div className="employeesByDeptSelectWrap" ref={containerRef}>
      <button
        type="button"
        className={`employeesByDeptSelectTrigger ${value ? "hasValue" : ""}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="employeesByDeptSelectTriggerText">
          {value ? label : placeholder}
        </span>
        <span className="material-icons employeesByDeptSelectTriggerIcon">expand_more</span>
      </button>
      {value && showChip && (
        <div className="employeesByDeptChips">
          <div className="employeesByDeptChip tagItem">
            <span className="selectedTag">{chipText}</span>
            <i
              className="material-icons selectedTagCloseIcon"
              onClick={e => {
                e.stopPropagation();
                if (onRemoveChip) onRemoveChip();
                else onChange(null);
              }}
              role="button"
              aria-label={`Remove ${chipText}`}
            >
              close
            </i>
          </div>
        </div>
      )}
      {open && (
        <div className="employeesByDeptDropdown">
          <div className="employeesByDeptSearchWrap">
            <input
              type="text"
              className="employeesByDeptSearchInput"
              placeholder={searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="employeesByDeptOptionList" role="listbox">
            {filtered.map(opt => {
              const isDisabled = excludedSet.has(opt.id) && opt.id !== value;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`employeesByDeptOptionItem ${isDisabled ? "isDisabled" : ""}`}
                  role="option"
                  aria-disabled={isDisabled}
                  onClick={() => selectOne(opt.id)}
                  disabled={isDisabled}
                >
                  {opt.label}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="employeesByDeptOptionEmpty">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const hasRangeError = row => {
  const start = row.start;
  const end = row.end;
  if (start == null || end == null) return false;
  return end < start;
};

const EMPTY_ROW = { function: [], start: null, end: null };

const EmployeesByDepartmentInputs = observer(() => {
  const list = dataSearchState?.filterComponentData?.employeesByDepartment ?? [];

  const setList = next => {
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      employeesByDepartment: next
    });
  };

  const updateRow = (index, row) => {
    const next = [...list];
    next[index] = row;
    setList(next);
  };

  const syncRangeErrors = () => {
    const errors = {};
    list.forEach((row, i) => {
      errors[i] = hasRangeError(row);
    });
    dataSearchState.setEmployeesByDepartmentErrors(errors);
  };

  React.useEffect(() => {
    syncRangeErrors();
  }, [list]);

  React.useEffect(() => {
    if (list.length === 0) {
      setList([{ ...EMPTY_ROW }]);
    }
  }, []);

  const addRow = () => {
    setList([...list, { ...EMPTY_ROW }]);
  };

  const removeRow = index => {
    const next = list.filter((_, i) => i !== index);
    setList(next);
  };

  const setDepartment = (index, id) =>
    updateRow(index, { ...list[index], function: id ? [id] : [] });
  const setStart = (index, v) => {
    const num = v === "" || v == null ? null : (Number(v) >= 0 ? Number(v) : null);
    updateRow(index, { ...list[index], start: num });
  };
  const setEnd = (index, v) => {
    const num = v === "" || v == null ? null : (Number(v) >= 0 ? Number(v) : null);
    updateRow(index, { ...list[index], end: num });
  };

  return (
    <div className="employeesByDeptSection">
      {list.map((row, index) => (
        <div key={index} className="employeesByDeptCard">
          <div className="employeesByDeptTopRow">
            <div className="employeesByDeptSelectContainer">
              <DepartmentSelect
                value={(row.function && row.function[0]) || null}
                onChange={id => setDepartment(index, id)}
                excludedIds={list.flatMap((r, j) => (j !== index ? (r.function || []) : []))}
                start={row.start}
                end={row.end}
                onRemoveChip={() => setDepartment(index, null)}
                placeholder="Department"
                searchPlaceholder="Search"
                showChip={false}
              />
            </div>
            {list.length > 1 && (
              <button
                type="button"
                className="employeesByDeptRemoveBtn"
                onClick={() => removeRow(index)}
                aria-label="Remove department"
              >
                <span className="material-icons">delete_outline</span>
              </button>
            )}
          </div>
          <div className="employeesByDeptRangeRow">
            <input
              type="number"
              min={0}
              placeholder="Min"
              className={`employeesByDeptRangeInput ${hasRangeError(row) ? "hasError" : ""}`}
              value={row.start ?? ""}
              onChange={e => setStart(index, e.target.value)}
              aria-label="Min employees"
              aria-invalid={hasRangeError(row)}
            />
            <span className="employeesByDeptRangeDash">-</span>
            <input
              type="number"
              min={0}
              placeholder="Max"
              className={`employeesByDeptRangeInput ${hasRangeError(row) ? "hasError" : ""}`}
              value={row.end ?? ""}
              onChange={e => setEnd(index, e.target.value)}
              aria-label="Max employees"
              aria-invalid={hasRangeError(row)}
            />
          </div>
          {hasRangeError(row) && (
            <div className="employeesByDeptRangeError" role="alert">
              Max must be greater than or equal to Min.
            </div>
          )}
        </div>
      ))}
      <button type="button" className="employeesByDeptAddBtn" onClick={addRow}>
        + Add Department
      </button>
    </div>
  );
});

export default EmployeesByDepartmentInputs;
export { DEPARTMENT_ID_TO_LABEL };
